// frontend/src/App.js
import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "react-query";
import { Toaster } from "react-hot-toast";
import { TenantProvider } from "./context/TenantContext";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { useTenant } from "./context/TenantContext";
import api from "./services/api";
import TenantHelmet from "./components/Common/TenantHelmet";
import Layout from "./components/Layout/Layout";
import ProtectedRoute from "./components/Common/ProtectedRoute";
import ScrollToTop from "./components/Common/ScrollToTop";
import LoadingSpinner from "./components/Common/LoadingSpinner";
import OfflineStoreOverlay from "./components/Common/OfflineStoreOverlay";
import MarketingTrackingManager from "./components/Common/MarketingTrackingManager";
import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductDetail from "./pages/ProductDetail";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import ResetPassword from "./pages/ResetPassword";
import SocialCallback from "./pages/SocialCallback";
import Reviews from "./pages/Reviews";
import BookingDetail from "./pages/BookingDetail";
import VerifyEmail from "./pages/VerifyEmail";
import PaymentCallback from "./pages/PaymentCallback";
import StorefrontPage from "./pages/StorefrontPage";

// Footer link pages
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import TrackOrder from "./pages/TrackOrder";
import CookiePolicy from "./pages/CookiePolicy";
import ShippingPolicy from "./pages/ShippingPolicy.jsx";
import ReturnsPolicy from "./pages/ReturnsPolicy.jsx";
import CancellationPolicy from "./pages/CancellationPolicy.jsx";
import Notifications from "./pages/Notifications";

import "./styles/globals.css";
import "./styles/navbar.css";
import "./styles/footer.css";
import "./styles/product.css";
import "./styles/auth.css";
import "./styles/home.css";
import "./styles/dashboard.css";
import "./styles/logout-modal.css";
import "./styles/checkout.css";
import "./styles/cart.css";
import "./styles/ResetPassword.css";
import "./styles/product-detail.css";
import "./styles/reviews-page.css";
import "./styles/cookie-consent.css";
import "./styles/cookie-policy.css";

// Page styles
import "./styles/about.css";
import "./styles/contact.css";
import "./styles/faq.css";
import "./styles/privacy.css";
import "./styles/terms.css";
import "./styles/track-order.css";
import "./styles/auth-slider.css";
import GoogleMapsProvider from "./pages/GoogleMapsProvider";
import { HelmetProvider } from "react-helmet-async";
import { useViewportOffset } from "./hooks/useViewportOffset";

const AdminLayout = lazy(() => import("./admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));
const Products = lazy(() => import("./admin/Products"));
const Suppliers = lazy(() => import("./admin/Suppliers"));
const AdminOrders = lazy(() => import("./admin/AdminOrders"));
const AdminCustomers = lazy(() => import("./admin/AdminCustomers"));
const AdminSettings = lazy(() => import("./admin/AdminSettings"));
const AdminProfile = lazy(() => import("./admin/AdminProfile"));
const AdminProductPromotions = lazy(
  () => import("./admin/AdminProductPromotions"),
);
const CategoryManager = lazy(() => import("./admin/CategoryManager"));
const AdminReports = lazy(() => import("./admin/AdminReports"));
const AdminResellers = lazy(() => import("./admin/AdminResellers"));
const AdminMarketingAds = lazy(() => import("./admin/AdminMarketingAds"));

// New Admin Components
const AdminReviews = lazy(() => import("./admin/AdminReviews"));
const AdminActivity = lazy(() => import("./admin/AdminActivity"));
const AdminAbandonedCarts = lazy(() => import("./admin/AdminAbandonedCarts"));
const Bookings = lazy(() => import("./admin/Bookings"));
const AdminGuide = lazy(() => import("./admin/AdminGuide"));
const AdminSupportTickets = lazy(() => import("./admin/AdminSupportTickets"));
const AdminStoreTemplates = lazy(
  () => import("./admin/AdminStoreTemplates"),
);
const AdminGettingStarted = lazy(
  () => import("./admin/AdminGettingStarted"),
);

const getStorefrontPageType = (pathname) => {
  if (pathname === "/") return "home";
  if (pathname === "/shop" || pathname.startsWith("/shop/")) return "shop";
  if (/^\/product\/[^/]+$/.test(pathname)) return "product";
  if (pathname === "/cart") return "cart";
  if (pathname === "/checkout") return "checkout";
  if (/^\/booking\/[^/]+$/.test(pathname)) return "booking";
  if (pathname === "/reviews") return "reviews";
  if (pathname === "/about") return "about";
  if (pathname === "/contact") return "contact";
  if (pathname === "/faq") return "faq";
  if (pathname === "/track-order") return "order-tracking";
  return null;
};

const createVisitorSessionId = () =>
  window.crypto?.randomUUID?.() ||
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    return (character === "x" ? random : (random & 0x3) | 0x8).toString(16);
  });

const getVisitorSessionId = (tenantCode) => {
  const storageKey = `storefront-visitor:${tenantCode}`;
  try {
    const existing = window.sessionStorage.getItem(storageKey);
    if (existing) return existing;
    const sessionId = createVisitorSessionId();
    window.sessionStorage.setItem(storageKey, sessionId);
    return sessionId;
  } catch {
    return createVisitorSessionId();
  }
};

const getVisitorMetadata = (pathname) => {
  const userAgent = navigator.userAgent || "";
  const deviceType = /ipad|tablet/i.test(userAgent)
    ? "tablet"
    : /mobi|iphone|ipod|android/i.test(userAgent)
      ? "mobile"
      : "desktop";
  const browser = /edg\//i.test(userAgent)
    ? "Edge"
    : /opr\//i.test(userAgent)
      ? "Opera"
      : /firefox\//i.test(userAgent)
        ? "Firefox"
        : /chrome\//i.test(userAgent)
          ? "Chrome"
          : /safari\//i.test(userAgent)
            ? "Safari"
            : "Other";

  let source = "Direct";
  let referrer = "";
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer);
      const referrerHost = referrerUrl.hostname.toLowerCase();
      referrer = referrerHost;
      if (referrerHost === window.location.hostname.toLowerCase()) {
        source = "Internal";
      } else if (/google|bing|yahoo|duckduckgo/.test(referrerHost)) {
        source = "Search";
      } else if (
        /facebook|instagram|tiktok|pinterest|linkedin|x\.com/.test(referrerHost)
      ) {
        source = "Social";
      } else {
        source = "Referral";
      }
    } catch {
      source = "Other";
    }
  }

  const query = new URLSearchParams(window.location.search);
  return {
    deviceType,
    browser,
    source,
    pagePath: pathname,
    referrer,
    locale: navigator.language || "",
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    campaignSource: query.get("utm_source") || "",
    campaignMedium: query.get("utm_medium") || "",
    campaignName: query.get("utm_campaign") || "",
  };
};

const hasVisitorAnalyticsConsent = () => {
  try {
    return (
      window.localStorage.getItem("cookieConsent") === "true" &&
      JSON.parse(window.localStorage.getItem("cookiePreferences") || "{}")
        .analytics === true
    );
  } catch (error) {
    console.warn("Could not read visitor analytics consent:", error);
    return false;
  }
};

const StorefrontActivityTracker = () => {
  const location = useLocation();
  const { tenantCode } = useTenant();
  const lastTrackedLocation = React.useRef(null);
  const [analyticsConsent, setAnalyticsConsent] = React.useState(
    hasVisitorAnalyticsConsent,
  );

  React.useEffect(() => {
    const updateAnalyticsConsent = () =>
      setAnalyticsConsent(hasVisitorAnalyticsConsent());
    window.addEventListener("cookieConsentUpdated", updateAnalyticsConsent);
    return () =>
      window.removeEventListener(
        "cookieConsentUpdated",
        updateAnalyticsConsent,
      );
  }, []);

  React.useEffect(() => {
    if (!tenantCode || !analyticsConsent) return;
    const pageType = getStorefrontPageType(location.pathname);
    if (!pageType) return;

    const locationKey = `${tenantCode}:${location.key}`;
    if (lastTrackedLocation.current === locationKey) return;
    lastTrackedLocation.current = locationKey;

    api
      .post("api/public/activity", {
        pageType,
        visitorSessionId: getVisitorSessionId(tenantCode),
        ...getVisitorMetadata(location.pathname),
      })
      .catch((error) => {
        console.warn("Could not record storefront page activity:", error);
      });
  }, [analyticsConsent, location.key, location.pathname, tenantCode]);

  return null;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60000,
    },
  },
});

// ============================================
// NOTE ON SUSPENSE PLACEMENT
// ============================================
// This AdminLoading fallback below is now ONLY used for the very first
// time a user navigates into "/admin" and the AdminLayout.jsx chunk
// itself is still downloading (since AdminLayout is lazy-loaded too).
//
// It is intentionally NOT used for navigation between admin child pages
// (Products, Orders, Customers, etc.) anymore. Previously this Suspense
// boundary wrapped the whole <AdminLayout /> element, so every time a
// lazy child route's chunk loaded, React treated the *entire* subtree
// (including the sidebar) as suspended and unmounted it, replacing it
// with this fallback — which is why the sidebar used to disappear on
// every menu click.
//
// The fix: AdminLayout.jsx now has its own inner <Suspense> that wraps
// only its <Outlet />. That inner boundary is closer to the lazy child
// routes, so it "catches" their loading state first, and the sidebar/
// header (which live outside that inner boundary) never unmount.
const AdminLoading = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      fontSize: "1.2rem",
      color: "#2c7da0",
    }}
  >
    <LoadingSpinner />
  </div>
);

const TenantAvailabilityGate = ({ children }) => {
  const { loading, tenant } = useTenant();
  const { pathname } = useLocation();
  const canAccessAdmin = pathname === "/login" || pathname.startsWith("/admin");

  if (!canAccessAdmin && loading) {
    return <AdminLoading />;
  }

  if (!canAccessAdmin && tenant?.IsActive === false) {
    return <OfflineStoreOverlay />;
  }

  return (
    <>
      {!canAccessAdmin && <OfflineStoreOverlay />}
      {children}
    </>
  );
};

function App() {
  useViewportOffset();

  return (
    <HelmetProvider>
      <GoogleMapsProvider>
        <QueryClientProvider client={queryClient}>
          <Router>
            <ScrollToTop />
            <TenantProvider>
              <AuthProvider>
                <CartProvider>
                  <StorefrontActivityTracker />
                  <MarketingTrackingManager />
                  {/* TenantHelmet - Updates meta tags dynamically based on tenant */}
                  <TenantHelmet />

                  <Toaster
                    position="top-center"
                    reverseOrder={false}
                    gutter={8}
                    containerStyle={{
                      position: "fixed",
                      top: 80,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 99999,
                      width: "auto",
                      maxWidth: "90vw",
                    }}
                    toastOptions={{
                      duration: 2000,
                      style: {
                        background: "#333",
                        color: "#ffffff",
                        fontSize: "14px",
                        borderRadius: "10px",
                        padding: "12px 20px",
                        fontWeight: "500",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        maxWidth: "380px",
                        width: "auto",
                        minWidth: "200px",
                      },
                      success: {
                        duration: 2000,
                        iconTheme: {
                          primary: "#10b981",
                          secondary: "#fff",
                        },
                        style: {
                          background: "#10b981",
                          color: "#ffffff",
                        },
                      },
                      error: {
                        duration: 2500,
                        iconTheme: {
                          primary: "#ef4444",
                          secondary: "#fff",
                        },
                        style: {
                          background: "#ef4444",
                          color: "#ffffff",
                        },
                      },
                      loading: {
                        duration: 1500,
                        style: {
                          background: "#3b82f6",
                          color: "#ffffff",
                        },
                      },
                    }}
                  />
                  <TenantAvailabilityGate><Routes>
                    {/* ✅ Social callback route - MUST be outside Layout to avoid tenant interference */}
                    <Route
                      path="/auth/social-callback"
                      element={<SocialCallback />}
                    />

                    {/* ✅ Email verification route - outside Layout for clean UI */}
                    <Route path="/verify-email" element={<VerifyEmail />} />

                    {/* ✅ Email verified callback route */}
                    <Route
                      path="/auth/email-verified"
                      element={<SocialCallback />}
                    />

                    {/* ✅ PAYMENT CALLBACK ROUTE - Outside Layout for clean UI */}
                    <Route
                      path="/payment/callback"
                      element={<PaymentCallback />}
                    />

                    {/* ✅ Public Routes with Layout */}
                    <Route path="/" element={<Layout />}>
                      <Route index element={<Home />} />
                      <Route path="shop" element={<Shop />} />
                      <Route path="product/:id" element={<ProductDetail />} />
                      <Route path="cart" element={<Cart />} />
                      <Route path="checkout" element={<Checkout />} />
                      <Route
                        path="order-confirmation/:orderId"
                        element={<OrderConfirmation />}
                      />
                      <Route path="login" element={<Auth />} />
                      <Route path="register" element={<Auth />} />
                      <Route
                        path="forgot-password"
                        element={<ForgotPassword />}
                      />
                      <Route
                        path="reset-password"
                        element={<ResetPassword />}
                      />
                      <Route
                        path="dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="notifications"
                        element={
                          <ProtectedRoute>
                            <Notifications />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="reviews" element={<Reviews />} />
                      <Route path="about" element={<About />} />
                      <Route path="contact" element={<Contact />} />
                      <Route path="faq" element={<FAQ />} />

                      {/* ✅ Legal & Policy Pages */}
                      <Route path="privacy" element={<Privacy />} />
                      <Route path="terms" element={<Terms />} />
                      <Route path="shipping" element={<ShippingPolicy />} />
                      <Route path="returns" element={<ReturnsPolicy />} />
                      <Route
                        path="cancellation-policy"
                        element={<CancellationPolicy />}
                      />
                      <Route path="cookies" element={<CookiePolicy />} />

                      <Route path="track-order" element={<TrackOrder />} />
                      <Route path="pages/:slug" element={<StorefrontPage />} />
                      <Route path="booking/:id" element={<BookingDetail />} />
                    </Route>

                    {/* ✅ Admin Routes */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute adminOnly={true}>
                          <Suspense fallback={<AdminLoading />}>
                            <AdminLayout />
                          </Suspense>
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<AdminDashboard />} />
                      <Route
                        path="getting-started"
                        element={<AdminGettingStarted />}
                      />
                      <Route path="products" element={<Products />} />
                      <Route path="suppliers" element={<Suppliers />} />
                      <Route path="orders" element={<AdminOrders />} />
                      <Route path="customers" element={<AdminCustomers />} />
                      <Route path="categories" element={<CategoryManager />} />
                      <Route path="bookings" element={<Bookings />} />
                      <Route path="settings" element={<AdminSettings />} />
                      <Route path="profile" element={<AdminProfile />} />
                      <Route
                        path="promotions"
                        element={<AdminProductPromotions />}
                      />
                      <Route
                        path="marketing"
                        element={<AdminMarketingAds />}
                      />
                      <Route
                        path="product-promotions"
                        element={<Navigate to="/admin/promotions" replace />}
                      />
                      <Route path="reports" element={<AdminReports />} />
                      <Route path="resellers" element={<AdminResellers />} />
                      <Route path="reviews" element={<AdminReviews />} />
                      <Route path="activity" element={<AdminActivity />} />
                      <Route path="notifications" element={<Notifications />} />
                      <Route
                        path="support-tickets"
                        element={<AdminSupportTickets />}
                      />
                      <Route
                        path="abandoned-carts"
                        element={<AdminAbandonedCarts />}
                      />
                      <Route
                        path="home-builder"
                        element={
                          <Navigate to="/admin/store-templates" replace />
                        }
                      />
                      <Route
                        path="store-templates"
                        element={<AdminStoreTemplates />}
                      />
                      <Route path="guide" element={<AdminGuide />} />
                    </Route>
                  </Routes></TenantAvailabilityGate>
                </CartProvider>
              </AuthProvider>
            </TenantProvider>
          </Router>
        </QueryClientProvider>
      </GoogleMapsProvider>
    </HelmetProvider>
  );
}

export default App;
